/* eslint-disable @typescript-eslint/naming-convention */
import { FC, useCallback, useEffect, useState } from 'react';

import { useGetUser } from 'app/api/hooks/core/v1/auth/user';
import { PropsWithOnlyChildren } from 'app/types';

import './theme.css';

const totalScriptCount = 3;
const script1Src = '//fw-cdn.com/10616630/3459368.js';
const script2Src = 'https://snippets.freshchat.com/js/fc-pre-chat-form-v2.js';
const script3Src = '//fw-cdn.com/1628939/2527989.js';

const FreshChatSetup: FC<PropsWithOnlyChildren> = props => {
  const [scriptLoadCount, setScriptLoadCount] = useState(0);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);

  const w = window;
  const d = document;
  const user = useGetUser();

  const handleScriptLoaded = useCallback(() => {
    if (scriptLoadCount < totalScriptCount) {
      setScriptLoadCount(scriptLoadCount + 1);
    }
    if (scriptLoadCount === totalScriptCount) {
      setIsScriptLoaded(true);
    }
  }, [scriptLoadCount]);

  // Inserts freshchat scripts once the window loads.
  useEffect(() => {
    const loadFreshChatScript = () => {
      const script1 = d.createElement('script');
      script1.src = script1Src;
      script1.async = true;
      script1.setAttribute('chat', 'true');
      script1.addEventListener('load', handleScriptLoaded);

      const script2 = d.createElement('script');
      script2.src = script2Src;
      script2.async = true;
      script2.addEventListener('load', handleScriptLoaded);

      const script3 = d.createElement('script');
      script3.src = script3Src;
      script3.async = true;
      script3.setAttribute('chat', 'true');
      script3.addEventListener('load', handleScriptLoaded);

      const firstScript = d.getElementsByTagName('script')[0];

      const existingScript1 = d.querySelector(`script[src="${script1Src}"]`);
      if (!existingScript1) {
        firstScript?.parentNode?.insertBefore(script1, firstScript);
      }

      const existingScript2 = d.querySelector(`script[src="${script2Src}"]`);
      if (!existingScript2) {
        firstScript?.parentNode?.insertBefore(script2, firstScript);
      }

      const existingScript3 = d.querySelector(`script[src="${script3Src}"]`);
      if (!existingScript3) {
        firstScript?.parentNode?.insertBefore(script3, firstScript);
      }
    };

    if (d.readyState === 'complete') {
      loadFreshChatScript();
    } else {
      w.addEventListener('load', loadFreshChatScript, false);

      return () => {
        w.removeEventListener('load', loadFreshChatScript, false);
      };
    }
  }, [w, d, handleScriptLoaded]);

  // Once scripts are loaded, configure the widget
  useEffect(() => {
    if (!isScriptLoaded) {
      return;
    }

    w.prechatTemplate = {
      SubmitLabel: 'Start Chat',
      fields: {
        0: {
          error: 'Please Enter a valid name',
          fieldId: 'name',
          label: 'Name',
          required: 'yes',
          type: 'text',
        },
        1: {
          error: 'Please Enter a valid Email',
          fieldId: 'email',
          label: 'Email',
          required: 'yes',
          type: 'email',
        },
        2: {
          error: 'Please Enter a valid Phone Number',
          fieldId: 'phone',
          label: 'Phone',
          required: 'no',
          type: 'phone',
        },
        3: {
          error: 'Please enter ID',
          fieldId: 'cf_mcdot',
          label: 'MC/DOT#',
          required: 'no',
          type: 'text',
        },
      },
      heading: 'Chat with Us',
      mainbgColor: '#00A4BF',
      maintxColor: '#fff',
      // eslint-disable-next-line @stylistic/max-len
      textBanner: 'We can\'t wait to talk to you. But first, please take a couple of moments to tell us a bit about yourself.',
    };

    w.fcSettings = {
      onInit() {
        if (w.fcPreChatform) {
          // Initialize the pre-chat form
          w.fcPreChatform.fcWidgetInit(w.prechatTemplate);
          if (user?.name) {
            const nameField = d.querySelector<HTMLInputElement>(
              'input[data-fieldid="name"]',
            );
            if (nameField) {
              nameField.value = user.name;
            }
            if (user?.email) {
              const emailField = d.querySelector<HTMLInputElement>(
                'input[data-fieldid="email"]',
              );
              if (emailField) {
                emailField.value = user.email;
              }
            }
            if (user?.phoneNumber) {
              const phoneField = d.querySelector<HTMLInputElement>(
                'input[data-fieldid="phone"]',
              );
              if (phoneField) {
                phoneField.value = user.phoneNumber;
              }
            }
            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
            const mcDot = user?.company?.mcNumber || user?.company?.dotNumber;
            if (mcDot) {
              const mcDotField = d.querySelector<HTMLInputElement>(
                'input[data-fieldid="cf_mcdot"]',
              );
              if (mcDotField) {
                mcDotField.value = mcDot;
              }
            }
          }
        }
      },
    };

    w.fcWidgetMessengerConfig = {
      config: {
        content: {
          actions: {
            tab_chat: 'Chat',
          },
          headers: {
            chat: 'Please Select a Business Type',
          },
        },
        cssNames: {
          expanded: 'custom_fc_expanded',
          widget: 'custom_fc_frame',
        },
      },
    };
  }, [isScriptLoaded, user, w, d]);

  return props.children;
};

export default FreshChatSetup;
